import { useState, useEffect, useCallback } from 'react'
import {
    FetchLiveDeviceData,
    FetchCalculatedData,
    FetchDeviceStatusV2,
    FetchAvailableDevices,
    FetchCalculatedInvertersForPlant,
    FetchAllInvertersLiveData,
} from '../../../../../services/api'

// Status icons
import DS2 from '../../../../../assets/images/DS2.svg' // Green
import DS3 from '../../../../../assets/images/DS3.svg' // Red

// Helper functions
const getStatusIcon = (status) => {
    if (!status) return DS3
    const s = status.toLowerCase()
    if (s === 'online' || s === 'live') return DS2
    return DS3
}

const formatValue = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '—') return '—'
    const num = parseFloat(value)
    if (isNaN(num)) return '—'
    return num.toFixed(decimals)
}

export const useLiveDataView = (plantData, token) => {
    const [fetchedData, setFetchedData] = useState({
        inverters: [],
        sensor: {
            sensorID: 'SNS-001',
            statusIcon: DS3,
            status: 'OFFLINE',
            modelName: '—',
            name: '—',
            temperature: { value: '—', unit: '°C' },
            irradiance: { value: '—', unit: 'W/m²' },
            irradiation: { value: '—', unit: 'kWh/m²' },
        },
    })

    const [calculatedData, setCalculatedData] = useState({
        meter: {
            meterID: 'MTR-001',
            statusIcon: DS3,
            status: 'OFFLINE',
            lastEnergyExport: { value: '—', unit: 'kWh' },
            current: { value: '—', unit: 'kWh' },
            multiplyingFactor: { value: '1', unit: 'x' },
            todayGeneration: { value: '—', unit: 'kWh' },
            name: '—',
        },
    })

    const [calculatedInverters, setCalculatedInverters] = useState([])
    const [availableDevices, setAvailableDevices] = useState({ inverters: 0, energyMeters: 0, sensors: 0 })
    const [loading, setLoading] = useState(false)

    const fetchDeviceStatus = useCallback(async () => {
        if (!plantData?.id || !token) return null
        try {
            const response = await FetchDeviceStatusV2(plantData.id, token)
            if (response?.success && response.data) {
                const statusData = response.data
                const statusMap = {
                    'MTR-001': statusData.energyMeter === 'online' ? 'ONLINE' : 'OFFLINE',
                    'SNS-001': statusData.sensor === 'online' ? 'ONLINE' : 'OFFLINE',
                }
                // For inverters, use the overall inverter status for simplicity or handle per inverter if needed
                // Requirements say: response includes status of all device types.
                // In status-v2, it returns { inverter: 'online'|'offline', sensor: ..., energyMeter: ... }
                return { statusMap, inverterStatus: statusData.inverter === 'online' ? 'ONLINE' : 'OFFLINE' }
            }
        } catch (error) {
            console.error('Error fetching device status:', error)
        }
        return null
    }, [plantData?.id, token])

    const fetchAvailableDevicesAction = useCallback(async () => {
        if (!plantData?.id || !token) return
        try {
            const response = await FetchAvailableDevices(plantData.id, token)
            if (response?.success && response.data) {
                setAvailableDevices(response.data)
                return response.data
            }
        } catch (error) {
            console.error('Error fetching available devices:', error)
        }
    }, [plantData?.id, token])

    const loadFetchedData = useCallback(async (statusResult, availableData) => {
        try {
            const statusMap = statusResult?.statusMap || {}
            const inverterStatus = statusResult?.inverterStatus || 'OFFLINE'

            if (availableData.inverters > 0) {
                const invertersResponse = await FetchAllInvertersLiveData(plantData.id, token)
                if (invertersResponse?.success && invertersResponse.data?.inverters) {
                    const transformedInverters = invertersResponse.data.inverters.map(inv => ({
                        inverterID: inv.deviceId,
                        channel: inv.channel,
                        statusIcon: getStatusIcon(inverterStatus), // Using simple logic for now
                        status: inverterStatus,
                        name: inv.name || 'Inverter',
                        model: inv.model || '—',
                        capacity: inv.capacity || '—',
                        power: { value: formatValue(inv.power), unit: 'kW', gapDays: inv.lastDataGapDays || 0 },
                        todayGeneration: { value: formatValue(inv.todayGeneration), unit: 'kWh', gapDays: inv.lastDataGapDays || 0 },
                    }))
                    setFetchedData(prev => ({ ...prev, inverters: transformedInverters }))
                }
            } else {
                setFetchedData(prev => ({ ...prev, inverters: [] }))
            }

            if (availableData.sensors > 0) {
                const sensorResponse = await FetchLiveDeviceData(plantData.id, 'SNS-001', token)
                if (sensorResponse?.success && sensorResponse.data) {
                    const sensorStatus = statusMap?.['SNS-001'] || 'OFFLINE'
                    const gap = sensorResponse.data.lastDataGapDays || 0
                    setFetchedData(prev => ({
                        ...prev, sensor: {
                            ...prev.sensor,
                            statusIcon: getStatusIcon(sensorStatus),
                            status: sensorStatus,
                            name: sensorResponse.data.name || 'Weather Sensor',
                            modelName: sensorResponse.data.model || '—',
                            temperature: { value: formatValue(sensorResponse.data.temperature), unit: '°C', gapDays: gap },
                            irradiance: { value: formatValue(sensorResponse.data.irradiance), unit: 'W/m²', gapDays: gap },
                            irradiation: { value: formatValue(sensorResponse.data.irradiation), unit: 'kWh/m²', gapDays: gap },
                        }
                    }))
                }
            }
        } catch (error) { console.error('Error loading fetched data:', error) }
    }, [plantData?.id, token])

    const loadCalculatedData = useCallback(async (statusResult, availableData) => {
        try {
            if (availableData.energyMeters > 0) {
                const meterResponse = await FetchCalculatedData(plantData.id, 'MTR-001', token)
                if (meterResponse?.success && meterResponse.data) {
                    const statusMap = statusResult?.statusMap || {}
                    const mf = meterResponse.data.multiplyingFactor || 1
                    const last = parseFloat(meterResponse.data.lastEnergyMeterTillExport) || 0
                    const curr = parseFloat(meterResponse.data.currentEnergyMeterTillExport) || 0
                    const todayGen = (curr - last) * mf
                    const mtrStatus = statusMap?.['MTR-001'] || 'OFFLINE'
                    const gap = meterResponse.data.lastDataGapDays || 1

                    setCalculatedData(prev => ({
                        ...prev, meter: {
                            ...prev.meter,
                            name: meterResponse.data.deviceName || 'Main Energy Meter',
                            statusIcon: getStatusIcon(mtrStatus),
                            status: mtrStatus,
                            lastEnergyExport: { value: formatValue(last), unit: 'kWh', gapDays: gap },
                            current: { value: formatValue(curr), unit: 'kWh' },
                            multiplyingFactor: { value: formatValue(mf, 1), unit: 'x' },
                            todayGeneration: { value: formatValue(todayGen), unit: 'kWh' },
                        }
                    }))
                }
            }
        } catch (error) { console.error('Error loading calculated data:', error) }
    }, [plantData?.id, token])

    const loadCalculatedInverters = useCallback(async (statusResult, availableData) => {
        try {
            if (availableData.inverters > 0) {
                const response = await FetchCalculatedInvertersForPlant(plantData.id, token)
                if (response?.success && response.data?.inverters) {
                    const inverterStatus = statusResult?.inverterStatus || 'OFFLINE'
                    const transformed = response.data.inverters.map(inv => {
                        const last = parseFloat(inv.lastTotalEnergy) || 0
                        const current = parseFloat(inv.currentTotalEnergy) || 0
                        const gap = inv.lastDataGapDays || 1
                        return {
                            inverterID: inv.deviceId,
                            channel: inv.channel,
                            statusIcon: getStatusIcon(inverterStatus),
                            status: inverterStatus,
                            last: { value: formatValue(last), unit: 'kWh', gapDays: gap },
                            current: { value: formatValue(current), unit: 'kWh' },
                            todayGeneration: { value: formatValue(current - last), unit: 'kWh' },
                            model: inv.model || '—',
                            name: inv.deviceName || 'Inverter',
                        }
                    })
                    setCalculatedInverters(transformed)
                } else {
                    setCalculatedInverters([])
                }
            } else {
                setCalculatedInverters([])
            }
        } catch (error) { console.error('Error loading calculated inverters:', error) }
    }, [plantData?.id, token])

    const handleRefresh = useCallback(async () => {
        setLoading(true)
        const [statusResult, availableData] = await Promise.all([
            fetchDeviceStatus(),
            fetchAvailableDevicesAction()
        ])
        if (availableData) {
            await Promise.all([
                loadFetchedData(statusResult, availableData),
                loadCalculatedData(statusResult, availableData),
                loadCalculatedInverters(statusResult, availableData)
            ])
        }
        setLoading(false)
    }, [fetchDeviceStatus, fetchAvailableDevicesAction, loadFetchedData, loadCalculatedData, loadCalculatedInverters])

    useEffect(() => {
        if (plantData?.id && token) {
            handleRefresh()
            const intervalId = setInterval(handleRefresh, 300000)
            return () => clearInterval(intervalId)
        }
    }, [plantData?.id, token, handleRefresh])

    return { fetchedData, calculatedData, calculatedInverters, availableDevices, loading, handleRefresh }
}
